import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Prose } from "@/components/Prose";
import { get, img, BRAND, COMPANY } from "@/lib/api";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams();
  const { data, isError, isLoading } = useQuery({ queryKey: ["article", slug], queryFn: () => get(`/articles/${slug}`) });
  if (isError) return <NotFound />;
  if (isLoading) return <div className="max-w-3xl mx-auto p-12 text-slate-600">Loading…</div>;
  const a = data.article;
  const crumbs = [{ name: "Home", to: "/" }, { name: "Articles", to: "/articles" }, { name: a.title, to: `/articles/${slug}` }];
  const ld = { "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, image: `${window.location.origin}${img(a.image)}`,
    datePublished: a.created_at, dateModified: a.updated_at, author: { "@type": "Organization", name: a.author },
    publisher: { "@type": "Organization", name: BRAND, legalName: COMPANY, logo: { "@type": "ImageObject", url: `${window.location.origin}/logo.png` } } };

  return (
    <article data-testid="article-page">
      <Seo title={a.title} description={a.excerpt} path={`/articles/${slug}`} image={img(a.image)} type="article" jsonLd={[ld, breadcrumbLd(crumbs)]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8"><Breadcrumbs items={crumbs} /></div>
      <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 fade-up">
        <p className="eyebrow">{a.category}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2150]" data-testid="article-title">{a.title}</h1>
        <p className="mt-4 text-base md:text-lg text-slate-600">{a.excerpt}</p>
        <p className="mt-4 text-sm text-slate-500">{a.author} · {new Date(a.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
      </header>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 my-10"><img src={img(a.image)} alt="" className="w-full h-72 sm:h-[420px] object-cover rounded-3xl" /></div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6"><Prose text={a.body} /></div>
      {data.related?.length > 0 && (
        <aside className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 sm:mt-16">
          <h2 className="text-2xl font-semibold text-[#0B2150] mb-6">Keep reading</h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {data.related.map((r) => (
              <Link key={r.slug} to={`/articles/${r.slug}`} className="group" data-testid={`related-article-${r.slug}`}>
                <img src={img(r.image)} alt="" className="h-40 w-full object-cover rounded-2xl" />
                <h3 className="mt-3 font-semibold text-[#0B2150] group-hover:underline">{r.title}</h3>
              </Link>
            ))}
          </div>
        </aside>
      )}
    </article>
  );
}
